import * as React from "react"
import { cn } from "@/utils"
import { Button } from "./button"
import { ChevronDown, ChevronRight, Plus, Eye } from "lucide-react"

export interface DetailSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  subtitle?: string
  collapsible?: boolean
  defaultCollapsed?: boolean
  actions?: {
    label: string
    icon?: React.ReactNode
    onClick: () => void
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    disabled?: boolean
  }[]
  variant?: "default" | "card" | "bordered"
  loading?: boolean
  empty?: boolean
  emptyMessage?: string
  emptyAction?: {
    label: string
    onClick: () => void
  }
}

const DetailSection = React.forwardRef<HTMLDivElement, DetailSectionProps>(
  ({ 
    className,
    title,
    subtitle,
    collapsible = false,
    defaultCollapsed = false,
    actions = [],
    variant = "default",
    loading = false,
    empty = false,
    emptyMessage = "Aucune donnée disponible",
    emptyAction,
    children,
    ...props 
  }, ref) => {
    const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed)

    const toggleCollapse = () => {
      if (collapsible) {
        setIsCollapsed(!isCollapsed)
      }
    }

    const getVariantClasses = () => {
      switch (variant) {
        case "card":
          return "bg-white rounded-lg border border-gray-200 shadow-sm"
        case "bordered":
          return "bg-white border border-gray-200 rounded-lg"
        default:
          return ""
      }
    }

    const getPaddingClasses = () => {
      switch (variant) {
        case "card":
        case "bordered":
          return "p-6"
        default:
          return ""
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          getVariantClasses(),
          className
        )}
        {...props}
      >
        {/* Header */}
        <div 
          className={cn(
            "flex items-center justify-between",
            getPaddingClasses(),
            variant === "default" && "pb-4 border-b border-gray-200",
            (collapsible || actions.length > 0) && "cursor-pointer"
          )}
          onClick={collapsible ? toggleCollapse : undefined}
        >
          <div className="flex items-center space-x-3">
            {collapsible && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleCollapse}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              {subtitle && (
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          {actions.length > 0 && (
            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || "outline"}
                  size="sm"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className="flex items-center space-x-2"
                >
                  {action.icon}
                  <span>{action.label}</span>
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        {!isCollapsed && (
          <div className={cn(
            getPaddingClasses(),
            variant === "default" && "pt-6"
          )}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="ml-3 text-gray-600">Chargement...</span>
              </div>
            ) : empty ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-gray-100 p-4 mb-4">
                  <Eye className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">{emptyMessage}</p>
                {emptyAction && (
                  <Button
                    variant="outline"
                    onClick={emptyAction.onClick}
                    className="flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{emptyAction.label}</span>
                  </Button>
                )}
              </div>
            ) : (
              children
            )}
          </div>
        )}
      </div>
    )
  }
)
DetailSection.displayName = "DetailSection"

export { DetailSection }